
// art.js — 2D canvas painters for X-Arcade Token Storm.
// Everything the 3D renderer textures from a <canvas>: the playfield artwork (base gradient, grain, neon
// beams, lamp inserts, label text and the multiply-blended shadow layer under every wall), the roughness
// map, the cabinet side art, the backglass translite, the soft "blob" sprite used for ball shadows and
// particles, and the tiny animated MiniScreen of the miniature cabinet.
// Depends on TABLE (table.js) for the playfield size in metres. No Three.js usage here.
// The painters started as a pixel-exact port of the shipped bundle. Phase 3 (PHASE3-PLAN V4/V8/V9/V14/A1)
// changed what is listed at each site: the playfield art is generated from the table data it decorates
// (lamp positions, pop collider radius, and the clear span between the walls around the title and around
// each label, so nothing is painted where a rail will cover it), the retired right ramp has no artwork,
// side-art text is never mirrored and the translite leaves room for the logo the renderer composites.

// Playfield artwork palette (hex strings usable as canvas fill/stroke styles).
const PALETTE = {
  bg0: "#08070d",
  bg1: "#0e0b16",
  red: "#ff2033",
  redDeep: "#a80f1e",
  magenta: "#ff2fa8",
  cyan: "#19e3ff",
  amber: "#ffb020",
  white: "#f4f2ff",
  steel: "#8a93a8",
};

// Insert lamp colours keyed by the `color` field of a lamp definition (table.lamps). The renderer tints the
// 3D lamp domes with the same table, so every colour name table.js uses must be listed here.
const LAMP_COLORS = {
  red: "#ff3040",
  amber: "#ffb020",
  cyan: "#19e3ff",
  magenta: "#ff2fa8",
  white: "#efeaff",
  warm: "#ffcf9a",
  violet: "#b46bff",
  green: "#39f07a",
};

// Text printed inside round inserts, keyed by lamp id: the A/C/E lane letters and the bonus multipliers.
const LAMP_TEXT = {
  laneA: "A",
  laneC: "C",
  laneE: "E",
  bonus2x: "2X",
  bonus3x: "3X",
  bonus4x: "4X",
  bonus5x: "5X",
  bonus6x: "6X",
};

// Collider layer masks a rolling ball touches (1, 3, 5); ramp-only layers (2, 4) carry no playfield artwork.
const BALL_LAYERS = new Set([1, 3, 5]);

/** Colour string for a lamp colour name; unknown names fall back to plain white. */
const lampColor = (name) => LAMP_COLORS[name] || "#ffffff";

/**
 * Strokes the path built by `tracePath` with a glowing neon look (stroke + same-colour shadow blur).
 * The canvas state is saved/restored around the call so blur and line width do not leak.
 */
function neonStroke(ctx, color, blur, lineWidth, tracePath) {
  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = blur;
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  tracePath();
  ctx.stroke();
  ctx.restore();
}

/** Deterministic LCG in [0,1) (Numerical Recipes constants) so texture grain is identical on every load. */
function seededRandom(seed) {
  let state = seed >>> 0;
  return () => (state = (state * 1664525 + 1013904223) >>> 0) / 4294967296;
}

/**
 * Largest font size (px) not above `maxPx` at which `text`, set in `weight` Arial, measures at most
 * `maxWidth` px. Glyph advance is linear in the font size, so one measurement is enough.
 */
function fitFontPx(ctx, text, weight, maxPx, maxWidth) {
  ctx.font = `${weight} ${maxPx}px Arial, sans-serif`;
  const width = ctx.measureText(text).width;
  return width > maxWidth ? maxPx * (maxWidth / width) : maxPx;
}

/**
 * Clear horizontal span [left, right] (metres) around x = xMid between the ball-level colliders that cross
 * the y band [yLo, yHi]; a side nothing bounds extends to the playfield edge. Used to size artwork so it stays
 * visible between 3D walls. A span narrower than `minWidth` means the band is blocked (a wall crossing xMid)
 * rather than merely tight, and the caller gets the unconstrained xMid +- 0.1 instead: pass the narrowest span
 * the artwork could still plausibly sit in — a hall of art like the title wants 0.12, a lane label 0.02.
 */
function clearSpan(colliders, yLo, yHi, xMid, minWidth = 0.12) {
  let left = 0;
  let right = TABLE.W;
  const note = (x) => {
    if (x < xMid) left = Math.max(left, x);
    else right = Math.min(right, x);
  };
  for (const col of colliders) {
    if (col.active === false || !BALL_LAYERS.has(col.layers)) continue;
    if (col.type === "seg") {
      const [a, b] = col.a.y <= col.b.y ? [col.a, col.b] : [col.b, col.a];
      if (b.y < yLo || a.y > yHi) continue;
      for (const p of [a, b]) if (p.y >= yLo && p.y <= yHi) note(p.x);
      for (const y of [yLo, yHi]) if (y > a.y && y < b.y) note(a.x + ((b.x - a.x) * (y - a.y)) / (b.y - a.y));
    } else if (col.type === "circle") {
      if (col.c.y + col.r < yLo || col.c.y - col.r > yHi) continue;
      note(col.c.x - col.r);
      note(col.c.x + col.r);
    }
  }
  return right - left < minWidth ? [xMid - 0.1, xMid + 0.1] : [left, right];
}

/**
 * Paints the playfield artwork onto a new width x height canvas.
 * `table` is the buildTable() result: meta.pops (pop bumper centres), world.colliders (shadow layer, pop
 * body radius, sling clearance), lamps (insert outlines), labels (text) and ramps (rampL colour).
 * Table coordinates are metres, y up from the flipper end.
 */
function paintPlayfield(width, height, table) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  const px = (x) => (x / TABLE.W) * width; // table x (m) -> texture px
  const py = (y) => (1 - y / TABLE.L) * height; // table y (m, up) -> texture px (down)
  const scale = width / TABLE.W; // texture px per metre
  const rand = seededRandom(777);
  const colliders = table.world.colliders;

  // Base: dark vertical gradient.
  const base = ctx.createLinearGradient(0, 0, 0, height);
  base.addColorStop(0, "#0c0a15");
  base.addColorStop(0.45, PALETTE.bg0);
  base.addColorStop(1, "#0a0810");
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, width, height);

  // Fine grain: 2600 faint white/black specks (three rand() calls each, colour first).
  ctx.globalAlpha = 0.05;
  for (let i = 0; i < 2600; i++) {
    ctx.fillStyle = rand() > 0.5 ? "#ffffff" : "#000000";
    ctx.fillRect(rand() * width, rand() * height, 1.3, 1.3);
  }
  ctx.globalAlpha = 1;

  // Faint diagonal cyan pinstripes across the whole sheet.
  ctx.save();
  ctx.globalAlpha = 0.05;
  ctx.strokeStyle = PALETTE.cyan;
  ctx.lineWidth = 1;
  for (let i = -20; i < 40; i++) {
    ctx.beginPath();
    ctx.moveTo(i * 90, 0);
    ctx.lineTo(i * 90 + height * 0.35, height);
    ctx.stroke();
  }
  ctx.restore();

  // Central hub: two crossed red beams (soft gradient bars + neon edge lines) rotated +-0.62 rad.
  const hubX = px(0.26);
  const hubY = py(0.5);
  ctx.save();
  ctx.translate(hubX, hubY);
  for (const [angle, tint] of [
    [-0.62, PALETTE.redDeep],
    [0.62, "#3b0d14"],
  ]) {
    ctx.save();
    ctx.rotate(angle);
    const beam = ctx.createLinearGradient(0, -height * 0.26, 0, height * 0.26);
    beam.addColorStop(0, "rgba(255,32,51,0)");
    beam.addColorStop(0.5, tint === PALETTE.redDeep ? "rgba(214,22,44,0.28)" : "rgba(150,16,34,0.20)");
    beam.addColorStop(1, "rgba(255,32,51,0)");
    ctx.fillStyle = beam;
    ctx.fillRect(-scale * 0.028, -height * 0.26, scale * 0.056, height * 0.52);
    ctx.restore();
  }
  for (const angle of [-0.62, 0.62]) {
    ctx.save();
    ctx.rotate(angle);
    neonStroke(ctx, PALETTE.red, 7, 1.8, () => {
      ctx.beginPath();
      ctx.moveTo(-scale * 0.028, -height * 0.22);
      ctx.lineTo(-scale * 0.028, height * 0.22);
      ctx.moveTo(scale * 0.028, -height * 0.22);
      ctx.lineTo(scale * 0.028, height * 0.22);
    });
    ctx.restore();
  }
  ctx.restore();

  // Storm burst: 14 alternating cyan/magenta lightning spokes radiating from the hub, each ending in a dot.
  ctx.save();
  ctx.globalAlpha = 0.32;
  for (let i = 0; i < 14; i++) {
    const theta = (i / 14) * Math.PI * 2 + 0.2;
    const innerR = scale * 0.1;
    const outerR = scale * (0.16 + (i % 4) * 0.03);
    const x0 = hubX + Math.cos(theta) * innerR;
    const y0 = hubY + Math.sin(theta) * innerR * 1.4;
    const x1 = hubX + Math.cos(theta) * outerR;
    const y1 = hubY + Math.sin(theta) * outerR * 1.5;
    const color = i % 2 ? PALETTE.cyan : PALETTE.magenta;
    const kink = scale * 0.01 * (i % 2 ? 1 : -1); // final zig alternates up/down
    neonStroke(ctx, color, 6, 1.4, () => {
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.lineTo(x1 + scale * 0.012, y1 - kink);
    });
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x1 + scale * 0.012, y1 - kink, 3.2, 0, 7);
    ctx.fill();
  }
  ctx.restore();

  // Title lettering above the flippers (A1): "X-ARCADE" sized to the clear span between the sling islands
  // (band y 0.225-0.26, walls at about x 0.13 / 0.32) and "TOKEN STORM" below it, between the return-lane
  // guides. Both are measured against the colliders so 3D geometry never hides their ends; caps are 0.72 em.
  const [islandL, islandR] = clearSpan(colliders, 0.225, 0.26, 0.23);
  const titleX = (islandL + islandR) / 2;
  const titlePx = fitFontPx(ctx, "X-ARCADE", 900, scale * 0.04, (islandR - islandL - 0.024) * scale);
  const titleBase = 0.2425 - (0.72 * titlePx) / scale / 2; // caps centred in the band
  const [guideL, guideR] = clearSpan(colliders, 0.205, 0.219, titleX);
  const subPx = fitFontPx(ctx, "T O K E N   S T O R M", 700, scale * 0.02, (guideR - guideL - 0.03) * scale);
  ctx.save();
  ctx.textAlign = "center";
  ctx.font = `900 ${titlePx}px Arial, sans-serif`;
  ctx.shadowColor = PALETTE.red;
  ctx.shadowBlur = 22;
  ctx.fillStyle = PALETTE.white;
  ctx.fillText("X-ARCADE", px(titleX), py(titleBase));
  ctx.font = `700 ${subPx}px Arial, sans-serif`;
  ctx.shadowBlur = 10;
  ctx.shadowColor = PALETTE.cyan;
  ctx.fillStyle = PALETTE.cyan;
  ctx.fillText("T O K E N   S T O R M", px(titleX), py(0.205));
  ctx.restore();

  // Amber halo ring around each pop bumper, 1.5 mm outside the popBody collider so it hugs the 3D skirt (V8)
  // whatever radius table.js builds the pops with.
  table.meta.pops.forEach(([popX, popY], i) => {
    const body = colliders.find((col) => col.tag === "popBody" + i);
    const ringR = (body ? body.r : 0.029) + 0.0015;
    ctx.save();
    ctx.globalAlpha = 0.55;
    neonStroke(ctx, PALETTE.amber, 10, 2, () => {
      ctx.beginPath();
      ctx.arc(px(popX), py(popY), scale * ringR, 0, 7);
    });
    ctx.restore();
  });

  // Shooter lane: cyan neon line plus five progressively brighter direction arrows (alpha 0.8 stays set
  // for the arrows because neonStroke restores to the state saved here).
  ctx.save();
  ctx.globalAlpha = 0.8;
  neonStroke(ctx, PALETTE.cyan, 8, 2, () => {
    ctx.beginPath();
    ctx.moveTo(px(0.48), py(0.1));
    ctx.lineTo(px(0.48), py(0.62));
  });
  for (let i = 0; i < 5; i++) {
    const y = 0.16 + i * 0.09;
    ctx.fillStyle = `rgba(25,227,255,${0.25 + i * 0.12})`;
    ctx.beginPath();
    ctx.moveTo(px(0.4725), py(y));
    ctx.lineTo(px(0.489), py(y));
    ctx.lineTo(px(0.4808), py(y + 0.022));
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  // Chevron stacks: `count` upward arrows at table (x,y), rotated rotDeg counter-clockwise from straight up,
  // brightening from the bottom one up. Inlane arrows, the left orbit lane, the spinner lane and the left
  // ramp approach (in the ramp's own colour); the retired right ramp gets none (V14).
  const chevrons = (x, y, rotDeg, color, count = 3, size = 1) => {
    ctx.save();
    ctx.translate(px(x), py(y));
    ctx.rotate((-rotDeg * Math.PI) / 180);
    for (let i = 0; i < count; i++) {
      ctx.globalAlpha = 0.35 + 0.2 * i;
      neonStroke(ctx, color, 5, 2, () => {
        ctx.beginPath();
        const offset = i * 13 * size;
        ctx.moveTo(-8 * size, -offset);
        ctx.lineTo(0, -offset - 9 * size);
        ctx.lineTo(8 * size, -offset);
      });
    }
    ctx.restore();
  };
  const rampL = (table.ramps || []).find((ramp) => ramp.tag === "rampL");
  chevrons(0.0506, 0.31, 0, PALETTE.white);
  chevrons(0.3936, 0.29, 0, PALETTE.white);
  chevrons(0.055, 0.46, 0, PALETTE.cyan);
  // The right orbit's approach lane (walls at x 0.412 / 0.454 here), so both orbits read as shots.
  chevrons(0.4325, 0.7, 0, PALETTE.cyan);
  chevrons(0.211, 0.73, 0, PALETTE.cyan);
  chevrons(0.2, 0.57, 25, lampColor(rampL ? rampL.color : "cyan"));

  // Shadow layer: every active ball-level collider (layers 1, 3, 5) casts a soft multiply-blended shadow
  // onto the art so walls read as raised. Ramp-only layers (2, 4) and colliders built inactive (the kick-out
  // doors, which the renderer does not rail either) are skipped. Colliders are drawn in array order.
  ctx.save();
  ctx.globalCompositeOperation = "multiply";
  ctx.strokeStyle = "rgba(0,0,0,0.55)";
  ctx.lineCap = "round";
  for (const col of colliders) {
    if (col.active === false || !BALL_LAYERS.has(col.layers)) continue;
    ctx.shadowColor = "rgba(0,0,0,0.9)";
    ctx.shadowBlur = 9;
    ctx.shadowOffsetY = 3;
    if (col.type === "seg") {
      ctx.lineWidth = Math.max(3, col.r * 2 * scale * 1.15);
      ctx.beginPath();
      ctx.moveTo(px(col.a.x), py(col.a.y));
      ctx.lineTo(px(col.b.x), py(col.b.y));
      ctx.stroke();
    } else if (col.type === "circle") {
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(px(col.c.x), py(col.c.y), Math.max(2, col.r * scale), 0, 7);
      ctx.stroke();
    } else if (col.type === "arc") {
      ctx.lineWidth = Math.max(3, col.r * 2 * scale * 1.15);
      let sweep = (col.a1 - col.a0) % (Math.PI * 2);
      if (sweep < 0) sweep += Math.PI * 2;
      ctx.beginPath();
      for (let i = 0; i <= 40; i++) {
        const ang = col.a0 + (sweep * i) / 40;
        const x = px(col.c.x + Math.cos(ang) * col.R);
        const y = py(col.c.y + Math.sin(ang) * col.R);
        if (i) ctx.lineTo(x, y);
        else ctx.moveTo(x, y);
      }
      ctx.stroke();
    }
  }
  ctx.restore();

  // Lamp inserts: translucent fill + bright outline. Arrows point lamp.rot degrees counter-clockwise from
  // straight up the table (0 = up, the chevron convention; rampLArrow 55 follows the left ramp); round
  // inserts come in three sizes, carry their LAMP_TEXT (lane letters, bonus multipliers) and the token
  // inserts get a coin-like inner ring. GI bulbs are not inserts and get no artwork.
  for (const lamp of table.lamps) {
    if (lamp.gi) continue;
    const color = lampColor(lamp.color);
    ctx.save();
    ctx.translate(px(lamp.x), py(lamp.y));
    if (lamp.shape === "arrow") {
      ctx.rotate((-(lamp.rot || 0) * Math.PI) / 180);
      ctx.globalAlpha = 0.28;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(0, -14);
      ctx.lineTo(9, 6);
      ctx.lineTo(3, 6);
      ctx.lineTo(3, 14);
      ctx.lineTo(-3, 14);
      ctx.lineTo(-3, 6);
      ctx.lineTo(-9, 6);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 0.9;
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = color;
      ctx.stroke();
    } else {
      const radius = lamp.big ? 13 : lamp.small ? 6.5 : 9;
      ctx.globalAlpha = 0.25;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(0, 0, radius, 0, 7);
      ctx.fill();
      ctx.globalAlpha = 0.9;
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.arc(0, 0, radius, 0, 7);
      ctx.stroke();
      const text = LAMP_TEXT[lamp.id];
      if (text) {
        ctx.globalAlpha = 0.95;
        ctx.fillStyle = color;
        ctx.font = `800 ${radius * (text.length > 1 ? 0.95 : 1.3)}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(text, 0, 1);
      } else if (/^token\d/.test(lamp.id)) {
        ctx.globalAlpha = 0.7;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.55, 0, 7);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  // Playfield labels (KICK, lane names, ...). label.size is in table metres, the caps sit centred on label.y.
  // Each label is kept inside the clear span of its own band the way the title is (A1): it slides the least
  // distance that clears both walls, and only if a wall still cuts into it does the type shrink (never below
  // 60% of the requested size). Labels with room around them are drawn exactly where the table asked. This
  // matters for the narrow lanes: "ORBIT" at (0.03, 0.75) is 28 mm of text in a 38 mm lane, so without the
  // slide its last letter is painted under the orbit guide and is hidden by that rail in the 3D views.
  const LABEL_PAD = 0.002; // metres of daylight between a label and the wall beside it
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const label of table.labels) {
    const capHalf = label.size * 0.36; // half the cap height: the band the lettering actually occupies
    const [spanL, spanR] = clearSpan(colliders, label.y - capHalf, label.y + capHalf, label.x, 0.02);
    const room = Math.max(0, (spanR - spanL - 2 * LABEL_PAD) * scale);
    const fontPx = Math.max(label.size * scale * 0.6, fitFontPx(ctx, label.text, 700, label.size * scale, room));
    ctx.save();
    ctx.font = `700 ${fontPx}px Arial`;
    const half = ctx.measureText(label.text).width / scale / 2;
    const x = Math.min(Math.max(label.x, spanL + LABEL_PAD + half), spanR - LABEL_PAD - half);
    ctx.fillStyle = "rgba(244,242,255,0.85)";
    ctx.shadowColor = PALETTE.magenta;
    ctx.shadowBlur = 6;
    ctx.fillText(label.text, px(x), py(label.y));
    ctx.restore();
  }

  // Soft white sheen low on the playfield.
  ctx.save();
  ctx.globalAlpha = 0.1;
  const sheen = ctx.createRadialGradient(px(0.26), py(0.16), 10, px(0.26), py(0.16), scale * 0.16);
  sheen.addColorStop(0, "#ffffff");
  sheen.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = sheen;
  ctx.fillRect(px(0.1), py(0.3), scale * 0.32, scale * 0.26);
  ctx.restore();
  return canvas;
}

/** Half-resolution roughness map: mid grey with 900 faint lighter/darker 2 px specks. */
function paintRoughness(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width / 2;
  canvas.height = height / 2;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#6e6e6e";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const rand = seededRandom(31);
  ctx.globalAlpha = 0.06;
  for (let i = 0; i < 900; i++) {
    ctx.fillStyle = rand() > 0.5 ? "#9a9a9a" : "#4a4a4a";
    ctx.fillRect(rand() * canvas.width, rand() * canvas.height, 2, 2);
  }
  return canvas;
}

/**
 * Cabinet side art: dark gradient, a red diagonal slash, a cyan neon streak and a tilted X-ARCADE
 * wordmark. `mirror` flips the slash and streak horizontally so the two sides of the cabinet are symmetric;
 * the wordmark is never mirrored (V9: the cabinet's box faces already read left-to-right from outside), it
 * just tilts the other way and sits at the mirrored anchor so it still follows the slash. The renderer maps
 * one unmirrored sheet onto both faces, so it never passes `mirror`; the flag is kept for callers that want
 * the flipped composition (and tests/art-v14-checks.mjs uses it to prove the lettering is not flipped).
 */
function paintSideArt(width, height, mirror = false) {
  const canvas = document.createElement('canvas');canvas.width=width;canvas.height=height;
  const ctx=canvas.getContext('2d');
  ctx.fillStyle='#101d26';ctx.fillRect(0,0,width,height);
  const sx=width/2000,sy=Math.min(height/460,sx);ctx.translate(0,(height-460*sy)/2);ctx.scale(sx,sy);
  ctx.fillStyle='#101d26';ctx.fillRect(0,0,2000,460);
  // Original screen-print composition: electric ribbons circling a minted storm token.
  const cx=mirror?390:1610,cy=222;
  const glow=ctx.createRadialGradient(cx,cy,20,cx,cy,620);
  glow.addColorStop(0,'#206c78');glow.addColorStop(.55,'#163641');glow.addColorStop(1,'#101d26');
  ctx.fillStyle=glow;ctx.fillRect(0,0,2000,460);
  ctx.save();ctx.beginPath();ctx.rect(0,0,2000,460);ctx.clip();
  for(let j=0;j<7;j++) {
    ctx.strokeStyle=j%2?'#e84535':'#56c9d0';ctx.lineWidth= j%2?13:4;
    ctx.beginPath();ctx.ellipse(cx,cy,150+j*51,70+j*23,-.28,0,Math.PI*2);ctx.stroke();
  }
  // Angular lightning ribbons deliberately placed around the title's quiet zone.
  for(const [x,y,size] of [[110,360,1],[830,70,.8],[1120,365,.7],[1820,50,.8]]) {
    ctx.save();ctx.translate(mirror?2000-x:x,y);ctx.scale(size,size);
    ctx.fillStyle='#f15136';ctx.beginPath();ctx.moveTo(-120,35);ctx.lineTo(70,-55);ctx.lineTo(40,-10);ctx.lineTo(170,-60);ctx.lineTo(10,65);ctx.lineTo(34,16);ctx.closePath();ctx.fill();ctx.restore();
  }
  // Token stacks and radiating cuts form the storm emblem; all artwork is authored here.
  for(let k=3;k>=0;k--) {
    const x=cx+k*15,y=cy+k*9;
    ctx.fillStyle=k?'#8d4b2d':'#e8b75c';ctx.strokeStyle='#ffdb88';ctx.lineWidth=4;
    ctx.beginPath();ctx.ellipse(x,y,112,112,-.2,0,7);ctx.fill();ctx.stroke();
  }
  ctx.strokeStyle='#74452a';ctx.lineWidth=4;ctx.beginPath();ctx.arc(cx,cy,91,0,7);ctx.stroke();
  for(let k=0;k<32;k++){const t=k/32*Math.PI*2;ctx.beginPath();ctx.moveTo(cx+Math.cos(t)*97,cy+Math.sin(t)*97);ctx.lineTo(cx+Math.cos(t)*107,cy+Math.sin(t)*107);ctx.stroke();}
  ctx.fillStyle='#513b2a';ctx.beginPath();ctx.moveTo(cx+20,cy-70);ctx.lineTo(cx-46,cy+8);ctx.lineTo(cx-8,cy+8);ctx.lineTo(cx-25,cy+65);ctx.lineTo(cx+51,cy-21);ctx.lineTo(cx+10,cy-21);ctx.closePath();ctx.fill();
  ctx.restore();
  // Opaque editorial band keeps lettering crisp over the illustrated storm.
  const tx=mirror?650:130;
  ctx.fillStyle='#0b151edd';ctx.beginPath();ctx.moveTo(tx-40,94);ctx.lineTo(tx+1150,94);ctx.lineTo(tx+1100,342);ctx.lineTo(tx-65,342);ctx.closePath();ctx.fill();
  ctx.fillStyle='#e94836';ctx.fillRect(tx,113,208,34);ctx.font='900 26px Arial';ctx.fillStyle='#fff0d2';ctx.fillText('X-ARCADE',tx+19,139);
  ctx.font='italic 900 157px Arial';ctx.lineJoin='round';ctx.lineWidth=10;ctx.strokeStyle='#060e16';
  const title='TOKEN STORM';const maxWidth=1080;
  ctx.strokeText(title,tx-5,293,maxWidth);
  const ink=ctx.createLinearGradient(0,165,0,300);ink.addColorStop(0,'#fff5d8');ink.addColorStop(.55,'#f5d594');ink.addColorStop(1,'#d79a4c');
  ctx.fillStyle=ink;ctx.fillText(title,tx-5,293,maxWidth);
  ctx.font='700 18px monospace';ctx.fillStyle='#8ed7da';ctx.fillText('HIGH VOLTAGE PINBALL  /  EVERY SHOT COUNTS',tx+4,327);
  // Border and small print belong to the decal, not a floating UI overlay.
  ctx.strokeStyle='#658a8a';ctx.lineWidth=2;ctx.strokeRect(22,22,1956,416);
  ctx.fillStyle='#ddbd7d';ctx.font='700 14px monospace';ctx.fillText('TS—01  /  X-ARCADE ORIGINAL',48,420);
  return canvas;
}

/** Front-panel wrap: artwork leaves the coin-door and control mounting zones legible. */
function paintFrontArt(width,height) {
  const canvas=document.createElement('canvas');canvas.width=width;canvas.height=height;
  const c=canvas.getContext('2d');c.fillStyle='#152731';c.fillRect(0,0,width,height);
  for(const side of [-1,1]) {
    c.save();c.translate(side<0?0:width,0);c.scale(side<0?1:-1,1);
    c.fillStyle='#dc4535';c.beginPath();c.moveTo(0,height*.14);c.lineTo(width*.31,height*.48);c.lineTo(width*.14,height*.5);c.lineTo(width*.31,height);c.lineTo(0,height*.65);c.closePath();c.fill();
    c.strokeStyle='#69bac2';c.lineWidth=4;c.beginPath();c.moveTo(0,height*.07);c.lineTo(width*.34,height*.45);c.stroke();c.restore();
  }
  c.font=`700 ${height*.043}px monospace`;c.fillStyle='#e8d1a5';c.textAlign='center';c.fillText('X-ARCADE  /  TOKEN STORM',width*.5,height*.97);
  return canvas;
}

/**
 * Backglass translite: radial purple glow, 26 alternating magenta/cyan rays from the panel centre and the
 * "TOKEN STORM" / "PINBALL" titles low on the panel. The X-Arcade wordmark is NOT painted here: the renderer
 * composites the real logo (V4) letterboxed at ~78% of the width and centred, whose lettering then covers
 * roughly 0.32-0.65 of the panel height, so the titles start at 0.72 and nothing overlaps.
 */
function paintTranslite(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  const glow = ctx.createRadialGradient(width / 2, height * 0.5, 20, width / 2, height * 0.5, width * 0.75);
  glow.addColorStop(0, "#251327");
  glow.addColorStop(0.6, "#120a18");
  glow.addColorStop(1, "#08060c");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);
  ctx.save();
  ctx.translate(width / 2, height * 0.5);
  for (let i = 0; i < 26; i++) {
    const angle = (i / 26) * Math.PI * 2;
    ctx.globalAlpha = 0.2;
    neonStroke(ctx, i % 2 ? PALETTE.magenta : PALETTE.cyan, 8, 2, () => {
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * 30, Math.sin(angle) * 30);
      ctx.lineTo(Math.cos(angle) * width * 0.62, Math.sin(angle) * width * 0.62);
    });
  }
  ctx.restore();
  ctx.textAlign = "center";
  ctx.font = `800 ${width * 0.062}px Arial`;
  ctx.fillStyle = PALETTE.amber;
  ctx.shadowColor = PALETTE.amber;
  ctx.shadowBlur = 14;
  ctx.fillText("T O K E N   S T O R M", width / 2, height * 0.8);
  ctx.font = `600 ${width * 0.03}px Arial`;
  ctx.fillStyle = PALETTE.cyan;
  ctx.shadowBlur = 8;
  ctx.shadowColor = PALETTE.cyan;
  ctx.fillText("PINBALL", width / 2, height * 0.89);
  return canvas;
}

/**
 * Square radial "blob" sprite: `coreColor` at the centre fading through half-white to transparent.
 * Used for ball shadows (dark core), particles and lamp glows.
 */
function makeBlobTexture(size = 64, coreColor = "#ffffff") {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  const glow = ctx.createRadialGradient(size / 2, size / 2, 1, size / 2, size / 2, size / 2);
  glow.addColorStop(0, coreColor);
  glow.addColorStop(0.35, "rgba(255,255,255,0.55)");
  glow.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, size, size);
  return canvas;
}

/**
 * The 96x64 animated screen of the miniature arcade cabinet: falling stars, a bouncing token and an
 * "INSERT COIN" ticker in attract mode; rainbow bars with "STORM" during multiball.
 */
class MiniScreen {
  constructor() {
    this.cv = document.createElement("canvas");
    this.cv.width = 96;
    this.cv.height = 64;
    this.ctx = this.cv.getContext("2d");
    this.t = 0;
    this.mode = "attract";
    // 26 stars scattered by a fixed stride, three fall speeds.
    this.stars = Array.from({ length: 26 }, (_, i) => ({
      x: (i * 37) % 96,
      y: (i * 23) % 64,
      s: 0.4 + (i % 3) * 0.5,
    }));
  }

  /** Advances the animation by dt seconds and repaints; returns the canvas. */
  draw(dt, multiball, achievement = null) {
    const { ctx, cv } = this;
    this.t += dt;
    ctx.fillStyle = "#050208";
    ctx.fillRect(0, 0, cv.width, cv.height);
    if (achievement) {
      const pulse=.5+.5*Math.sin(this.t*12);
      ctx.strokeStyle='#21dccc';ctx.lineWidth=2;ctx.strokeRect(3,3,90,58);
      ctx.fillStyle='#ffcd70';ctx.font='900 10px monospace';ctx.textAlign='center';
      ctx.fillText(achievement,48,27);
      ctx.fillStyle='#ffffff';ctx.font='700 8px monospace';ctx.fillText('STORM UNLEASHED',48,42);
      ctx.fillStyle=`rgba(33,220,204,${.25+pulse*.35})`;ctx.fillRect(8,50,80,3);
      return cv;
    }
    if (multiball) {
      for (let row = 0; row < 8; row++) {
        ctx.fillStyle = `hsl(${(this.t * 120 + row * 45) % 360},95%,55%)`;
        ctx.fillRect(0, row * 8, 96, 7);
      }
      ctx.fillStyle = "#000";
      ctx.font = "900 15px monospace";
      ctx.textAlign = "center";
      ctx.fillText("STORM", 48, 38);
      return cv;
    }
    for (const star of this.stars) {
      star.y += star.s * dt * 22;
      if (star.y > 64) {
        star.y = -1;
        star.x = (star.x + 31) % 96;
      }
      ctx.fillStyle = `rgba(140,220,255,${0.3 + star.s * 0.4})`;
      ctx.fillRect(star.x | 0, star.y | 0, 1.6, 1.6);
    }
    // Token swings side to side and bounces (|sin| folded so it never goes below the baseline).
    const coinX = 48 + Math.sin(this.t * 1.7) * 30;
    const coinY = 26 + Math.abs(Math.sin(this.t * 2.6)) * -14 + 14;
    ctx.fillStyle = "#ffb020";
    ctx.beginPath();
    ctx.arc(coinX, coinY, 5, 0, 7);
    ctx.fill();
    ctx.fillStyle = "#7a4d00";
    ctx.font = "700 7px monospace";
    ctx.textAlign = "center";
    ctx.fillText("X", coinX, coinY + 2.5);
    // Ticker scrolls right-to-left and wraps once a full message width has passed (6 px per glyph).
    const ticker = "INSERT COIN \u2014 X-ARCADE \u2014 ";
    ctx.font = "700 9px monospace";
    const scroll = (this.t * 34) % (ticker.length * 6);
    ctx.fillStyle = "#19e3ff";
    ctx.fillText(ticker + ticker, 96 - scroll, 58);
    return cv;
  }
}

/** Original silkscreen for the die-cut sling and return plastics; coordinates stay in playfield metres. */
function paintPlayfieldPlastic(spec) {
  const xs=spec.points.map(p=>p[0]),ys=spec.points.map(p=>p[1]);
  const bounds={x0:Math.min(...xs),y0:Math.min(...ys),w:Math.max(...xs)-Math.min(...xs),h:Math.max(...ys)-Math.min(...ys)};
  const canvas=document.createElement('canvas');canvas.width=768;canvas.height=1536;
  const c=canvas.getContext('2d'),sx=canvas.width/bounds.w,sy=canvas.height/bounds.h;
  const path=()=>{c.beginPath();spec.points.forEach(([x,y],i)=>c[i?'lineTo':'moveTo'](x,y));c.closePath();};
  c.setTransform(sx,0,0,-sy,-bounds.x0*sx,(bounds.y0+bounds.h)*sy);
  path();c.clip();c.fillStyle='#142638';c.fillRect(bounds.x0,bounds.y0,bounds.w,bounds.h);
  const left=spec.side==='L',mirror=x=>left?x:.453-x;
  const polygon=(points,color)=>{c.beginPath();points.forEach(([x,y],i)=>c[i?'lineTo':'moveTo'](mirror(x),y));c.closePath();c.fillStyle=color;c.fill();};
  if(spec.type==='apron'){
    polygon([[0,.81],[.016,.77],[.008,.69],[.04,.67],[.020,.61],[.022,.51],[.005,.60],[.014,.66],[0,.68]],'#23bcca');
    polygon([[0,.70],[.034,.69],[.018,.64],[.033,.60],[.008,.62],[.015,.67]],'#c72d4b');
    c.save();c.translate(.018,.61);c.scale(.0001,-.0001);c.rotate(-Math.PI/2);c.fillStyle='#f5e7c7';c.font='bold 48px Arial';c.textAlign='center';c.fillText('STORM CIRCUIT',0,0);c.restore();
  }else if(spec.type==='sling'){
    polygon([[.063,.350],[.103,.350],[.097,.309],[.116,.295],[.098,.264],[.133,.215],[.064,.215]],'#c62643');
    polygon([[.085,.341],[.108,.317],[.098,.293],[.119,.277],[.105,.278],[.119,.241],[.089,.280],[.103,.296],[.086,.313]],'#f3e9cf');
    polygon([[.077,.339],[.096,.313],[.088,.292],[.108,.269],[.091,.280],[.078,.306]],'#21c7d3');
    // Concentric token stamp, a small printed feature rather than another glowing object.
    const x=mirror(.101),y=.256;
    c.strokeStyle='#e2bc70';c.lineWidth=.0011;
    for(const r of [.0075,.0058]){c.beginPath();c.arc(x,y,r,0,Math.PI*2);c.stroke();}
    c.save();c.translate(x,y);c.scale(1,-1);c.fillStyle='#f9dfaa';c.scale(.0001,.0001);c.font='bold 60px Arial';c.textAlign='center';c.textBaseline='middle';c.fillText('X',0,0);c.restore();
    c.save();c.translate(mirror(.089),.318);c.scale(1,-1);c.rotate(left?-.17:.17);c.fillStyle='#ffffff';c.scale(.0001,.0001);c.font='bold 42px Arial';c.textAlign='center';c.fillText('STORM',0,0);c.restore();
  }else{
    polygon([[.027,.209],[.119,.141],[.146,.158],[.046,.231]],'#d72c49');
    polygon([[.029,.201],[.078,.183],[.061,.179],[.130,.146],[.094,.176],[.110,.177],[.041,.222]],'#20cad5');
    c.save();c.translate(mirror(.083),.191);c.scale(1,-1);c.rotate(left?.60:-.60);
    c.fillStyle='#112536';c.fillRect(-.033,-.0035,.066,.007);
    c.fillStyle='#f5e7c7';c.scale(.0001,.0001);c.font='bold 46px Arial';c.textAlign='center';c.textBaseline='middle';c.fillText('TOKEN RETURN',0,0);c.restore();
  }
  // A fine ivory keyline and a dark edge protect the print beneath the polished sheet.
  path();c.strokeStyle='#f5e4bb';c.lineWidth=.0022;c.stroke();
  path();c.strokeStyle='#152334';c.lineWidth=.0006;c.stroke();
  return {canvas,bounds};
}
